import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import CrmLayout from "../../components/CrmLayout";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Check } from "lucide-react";

export default function Billing() {
  const { salon, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [period, setPeriod] = useState("month");
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/billing").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { if (params.get("canceled")) toast.info("Checkout canceled."); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data: st } = await api.get(`/payments/status/${sid}`);
        if (st.payment_status === "paid") { toast.success("Subscription active! Thank you."); await refresh(); load(); setParams({}); return; }
        if (st.status === "expired" || tries > 8) { toast.error("Payment not completed."); setParams({}); return; }
      } catch { if (tries > 8) { setParams({}); return; } }
      setTimeout(poll, 2000);
    };
    poll();
  }, []); // eslint-disable-line

  const subscribe = async (tier) => {
    setBusy(tier);
    try {
      const { data: res } = await api.post("/billing/checkout", { tier, interval: period, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const changePlan = async (tier) => {
    setBusy(tier);
    try { await api.post("/billing/change", { tier, interval: period }); toast.success("Plan updated"); await refresh(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const cancelPlan = async () => {
    setBusy("cancel");
    try { await api.post("/billing/cancel"); toast.success("Subscription will cancel at period end"); await refresh(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const resumePlan = async () => {
    setBusy("resume");
    try { await api.post("/billing/resume"); toast.success("Subscription resumed"); await refresh(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const manageBilling = async () => {
    setBusy("portal");
    try {
      const { data: res } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = res.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(""); }
  };

  const sub = data?.subscription || salon?.subscription || {};
  const tiers = data?.tiers || {};
  const order = ["starter", "professional", "premium"];
  const isActive = sub.status === "active";

  return (
    <CrmLayout title="Billing">
      <div className="bg-card border border-border rounded-lg p-6 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow text-muted-foreground">Current plan</div>
          <div className="font-display text-2xl font-bold mt-1 capitalize">
            {isActive ? (tiers[sub.tier]?.name || sub.tier) : "Free trial"}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            {isActive
              ? (sub.cancel_at_period_end ? "Cancels at the end of the current period" : `Renews ${sub.interval === "year" ? "yearly" : "monthly"}`)
              : sub.trial_end ? `Trial ends ${new Date(sub.trial_end).toLocaleDateString()}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isActive && <Button variant="outline" size="sm" data-testid="manage-billing-btn" disabled={busy === "portal"} onClick={manageBilling}>{busy === "portal" ? "Opening…" : "Manage payment & invoices"}</Button>}
          {isActive && (sub.cancel_at_period_end
            ? <Button variant="outline" size="sm" data-testid="resume-btn" disabled={busy === "resume"} onClick={resumePlan}>Resume plan</Button>
            : <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/5" data-testid="cancel-btn" disabled={busy === "cancel"} onClick={cancelPlan}>Cancel subscription</Button>)}
          <Badge className={isActive ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-accent/20 text-accent-foreground"}>{sub.cancel_at_period_end ? "canceling" : sub.status}</Badge>
        </div>
      </div>

      <div className="flex items-center justify-center mb-6">
        <div className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-card">
          <button data-testid="bill-period-month" onClick={() => setPeriod("month")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${period === "month" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
          <button data-testid="bill-period-year" onClick={() => setPeriod("year")} className={`px-4 py-1.5 rounded-full text-sm transition-colors ${period === "year" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Yearly · 2 months free</button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 items-start">
        {order.map((id) => {
          const t = tiers[id];
          if (!t) return null;
          const current = isActive && sub.tier === id && (sub.interval || "month") === period;
          const popular = id === "professional";
          const price = period === "year" ? t.price_yearly : t.price;
          const btnLabel = current ? "Current plan" : busy === id ? "Please wait…" : isActive ? "Switch to this plan" : "Subscribe";
          return (
            <div key={id} data-testid={`plan-${id}`} className={`rounded-xl border p-6 bg-card relative ${popular ? "border-primary shadow-lg" : "border-border"}`}>
              {popular && <span className="absolute -top-3 left-6 bg-accent text-accent-foreground text-xs font-semibold px-3 py-1 rounded-full">Most popular</span>}
              <h3 className="font-display text-lg font-semibold">{t.name}</h3>
              <div className="mt-2 flex items-baseline gap-1"><span className="font-display text-4xl font-bold">${price}</span><span className="text-muted-foreground text-sm">/{period === "year" ? "yr" : "mo"}</span></div>
              <Button className={`w-full mt-4 rounded-md ${current ? "bg-secondary text-secondary-foreground" : popular ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-secondary text-secondary-foreground hover:bg-secondary/70"}`}
                data-testid={`subscribe-${id}`} disabled={current || !!busy} onClick={() => (isActive ? changePlan(id) : subscribe(id))}>
                {btnLabel}
              </Button>
              <ul className="mt-5 space-y-2.5">
                {t.features.map((f) => <li key={f} className="flex items-start gap-2 text-sm"><Check size={15} className="text-primary mt-0.5 shrink-0" />{f}</li>)}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-6">Test mode — use card 4242 4242 4242 4242, any future expiry and CVC.</p>
    </CrmLayout>
  );
}
